import { Box, inputBaseClasses, Paper, styled, useTheme } from "@mui/material";
import { gridClasses, GridColDef, GridRenderEditCellParams, useGridApiContext } from "@mui/x-data-grid";
import { DatePicker } from "@mui/x-date-pickers";
import { GetFieldLookupItem, useFormMetadata, useFormSave, useLookups } from "@ngt/forms";
import { DateTime } from "luxon";
import { FC, useContext, useMemo } from "react";
import { Outlet, useParams } from "react-router-dom";
import { Contract, FormDefinitions } from "../api/dtos";
import CollapsibleTablePremium from "../components/tables/CollapsibleTablePremium";
import TitleBox from "../components/TitleBox";
import { createCustomToolbarProps } from "../components/utility/CustomToolbarProps";
import ProjectContext from "../contexts/data/ProjectContext";
import StageContext from "../contexts/data/StageContext";
import useContractsByStageId from "../hooks/data/useContractsByStageId";

const StyledPaper = styled(Paper) (({theme}) => ({
    marginBottom: theme.spacing(2)
}));

const DataGridDatePicker:FC<GridRenderEditCellParams> = (params) => {
    const theme = useTheme();

    const apiRef = useGridApiContext();

    const handleValueChange = (value: any) => {
        const newValue = value as DateTime; // The new value entered by the user
        apiRef.current.setEditCellValue({ id: params.id, field: params.field, value: newValue?.toISO({includeOffset: false})});
    };

    return (
        <DatePicker
            format="dd/MM/yyyy"
            slotProps={{
                textField: {
                    fullWidth: true,
                    variant: "standard",
                    sx: {
                        alignSelf: 'center',
                        flex: '1 1 auto',
                        justifyContent: 'stretch',
                        border: "none",
                        margin: theme.spacing(0, 1),
                        [`& .${inputBaseClasses.root}::before`]: {
                            display: 'none'
                        },
                        [`& .${inputBaseClasses.root}::after`]: {
                            display: 'none'
                        }
                    },
                    slotProps: {
                        input: {
                            disableUnderline: true
                        },
                    }
                }
            }}
            value={params?.value ? DateTime.fromISO(params.value) : null}
            onChange={(value, _context) => handleValueChange(value as any)}
        />
    );
}

const useContractColumns = () => {

    const metadata = useFormMetadata(FormDefinitions.Contract);

    const {data: lookups, loading: _lookupsLoading} = useLookups(metadata);

    const contractColumns = useMemo(() => {
        const columns: GridColDef<Contract>[] =  [
            {
                field: "lotId",
                headerName: "Lot Number",
                valueGetter: (_value, row) => GetFieldLookupItem(lookups, "LotId", row.lotId)?.value,
                flex: 1
            },
            {
                field: "status",
                headerName: "Contract Status",
                valueGetter: (_value, row) => GetFieldLookupItem(lookups, "Status", row.status)?.value,
                flex: 1
            },
            {
                field: "agentLocationId",
                headerName: "Agent",
                valueGetter: (_value, row) => GetFieldLookupItem(lookups, "AgentLocationId", row.agentLocationId)?.value,
                flex: 1
            },
            {
                field: "price",
                headerName: "Price",
                editable: true,
                flex: 1
            },
            {
                field: "issueDate",
                headerName: "Issue Date",
                valueFormatter: (_value, row) => {
                    return row.issueDate ? DateTime.fromISO(row.issueDate).toFormat("dd/MM/yyyy") : undefined;
                },
                renderEditCell: (params: GridRenderEditCellParams) => (
                    <DataGridDatePicker
                        {...params?? {}}
                        value={params.value ? params.value : ""} 
                    />
                ),
                editable: true,
                flex: 1
            },
            {
                field: "exhangeDate",
                headerName: "Exchange Date",
                valueFormatter: (_value, row) => {
                    return row.exchangeDate ? DateTime.fromISO(row.exchangeDate).toFormat("dd/MM/yyyy") : undefined;
                },
                renderEditCell: (params: GridRenderEditCellParams) => (
                    <DataGridDatePicker
                        {...params?? {}}
                        value={params.value ? params.value : ""} 
                    />
                ),
                editable: true,
                flex: 1
            },
            {
                field: "expectedSettleDate",
                headerName: "Expected Settle Date",
                valueFormatter: (_value, row) => {
                    return row.expectedSettleDate ? DateTime.fromISO(row.expectedSettleDate).toFormat("dd/MM/yyyy") : undefined;
                },
                renderEditCell: (params: GridRenderEditCellParams) => (
                    <DataGridDatePicker
                        {...params?? {}}
                        value={params.value ? params.value : ""} 
                    />
                ),
                editable: true,
                flex: 1
            },
            {
                field: "settleDate",
                headerName: "Settle Date",
                valueFormatter: (_value, row) => {
                    return row.settleDate ? DateTime.fromISO(row.settleDate).toFormat("dd/MM/yyyy") : undefined;
                },
                renderEditCell: (params: GridRenderEditCellParams) => (
                    <DataGridDatePicker
                        {...params?? {}}
                        value={params.value ? params.value : ""} 
                    />
                ),
                editable: true,
                flex: 1
            }
        ];

        return columns;
    }, [lookups]);

    return contractColumns;
}

export const BulkEditContracts: FC = () => {
    const contractMetadata= useFormMetadata(FormDefinitions.Contract);

    const save = useFormSave(contractMetadata);

    const theme = useTheme();
    
    const {data: _contractLookups, loading: _lookupsLoading} = useLookups(contractMetadata);
    
    const params = useParams();

    const {data: project} = useContext(ProjectContext);
    const {data: stage} = useContext(StageContext);
    
    const stageId = Number(params.stageId);
        
    const {data: contracts, loading: contractsLoading} = useContractsByStageId(stageId);

    const submit = (newContract: Contract) => {
        save(newContract);
        return newContract;
    }
    
    const customToolbarProps = useMemo(() => createCustomToolbarProps({
        exportTitle: `${project?.name} - ${stage?.name} - Contracts`,
        fileName: `${project?.name} - ${stage?.name} - Contracts`,
        includeDateTime: true
    }), [project, stage]);
    
    
    const columns = useContractColumns();

    return (
        <>
            <Outlet />

            <TitleBox>
            </TitleBox>

            <StyledPaper>
                <Box
                    sx={{
                        [`& .${gridClasses.root}`]: {
                            
                            [`& .${gridClasses.columnHeader}`]: {
                                height: 'auto !important',
                                minHeight: 56,
                                
                                [`& .${gridClasses.columnHeaderTitleContainer}`]: {
                                    whiteSpace: 'normal',
                                    
                                    [`& .${gridClasses.columnHeaderTitle}`]: {
                                        whiteSpace: 'normal',
                                        paddingTop: theme.spacing(1.5),
                                        paddingBottom: theme.spacing(1.5) 
                                    }
                                },
                                
                                [`& .${gridClasses.iconButtonContainer}`]: {
                                    width: 'unset',
                                },
                                
                                '&.column-group-header': {
                                    backgroundColor: theme.palette.grey[300],
                                    boxShadow: "1",
                                    textAlign: 'center'
                                }
                            }
                        },
                    }}
                    >
                    <CollapsibleTablePremium
                        density="compact"
                        editMode="row"
                        processRowUpdate={(newContract, _oldRow, _params) => submit(newContract)}
                        //onRowEditStop={(params, event) => contractEditSubmit(params, event)}
                        startCollapsed={false} 
                        title={"Contracts"} 
                        loading={contractsLoading} 
                        columns={columns}
                        rows={contracts}
                        disableColumnMenu={false}
                        disableColumnFilter={false}
                        {...customToolbarProps}
                    />
                </Box>
            </StyledPaper>
        </>
    );
}